/**
 * Base configuration validation schemas using Zod
 *
 * This module defines the core validation schemas for environment configuration
 * that are shared across all applications in the monorepo.
 */
import { z } from 'zod';
/**
 * Environment variable schema for common configuration
 * All apps extend this base schema with app-specific variables
 */
export const baseEnvSchema = z.object({
    // Environment detection
    NODE_ENV: z
        .enum(['development', 'staging', 'production', 'test'])
        .default('development')
        .describe('Application environment: development, staging, production, or test'),
    ENVIRONMENT: z
        .enum(['development', 'staging', 'production', 'test'])
        .optional()
        .describe('Alternative environment variable (fallback if NODE_ENV not set)'),
    // Server configuration
    PORT: z.coerce
        .number()
        .int()
        .min(1, 'PORT must be between 1 and 65535')
        .max(65535, 'PORT must be between 1 and 65535')
        .default(8080)
        .describe('HTTP server port (1-65535)'),
    // Version information
    VERSION: z.string().default('0.0.1').describe('Application version (semantic version format)'),
    // Database configuration
    DATABASE_URL: z
        .string()
        .min(1, 'DATABASE_URL is required')
        .regex(/^postgresql:\/\//i, 'DATABASE_URL must be a valid PostgreSQL connection string (must start with postgresql://)')
        .describe('PostgreSQL database connection string'),
    // Redis configuration (optional)
    REDIS_URL: z
        .string()
        .url('REDIS_URL must be a valid URL')
        .optional()
        .describe('Redis connection URL (optional)'),
    // CORS configuration
    CORS_ORIGINS: z
        .string()
        .default('http://localhost:3000')
        .describe('Comma-separated list of allowed CORS origins'),
    // Logging configuration
    LOG_LEVEL: z
        .enum(['debug', 'info', 'warn', 'error'])
        .default('info')
        .describe('Logging verbosity level'),
    // Rate limiting configuration
    RATE_LIMIT_MAX: z.coerce
        .number()
        .int()
        .positive('RATE_LIMIT_MAX must be greater than 0')
        .default(500)
        .describe('Maximum requests per rate limit window (per IP, unauthenticated)'),
    RATE_LIMIT_WINDOW_MS: z.coerce
        .number()
        .int()
        .positive('RATE_LIMIT_WINDOW_MS must be greater than 0')
        .default(60000)
        .describe('Rate limit window duration in milliseconds'),
    // GitHub Issues Integration
    GITHUB_FETCH_LIMIT: z.coerce
        .number()
        .int()
        .positive()
        .default(5)
        .describe('Maximum number of GitHub issues to fetch per PR'),
    GITHUB_CACHE_TTL: z.coerce
        .number()
        .int()
        .positive()
        .default(300)
        .describe('Cache TTL for GitHub issues in seconds'),
});
//# sourceMappingURL=validation.js.map